import Router from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { responsiveBreakpointMap } from '~/constants'
import locale from '~/locale'
import { RootState } from '~/redux/store'

import { version as pkgVersion } from '../../package.json'

/**
 * Detects a new app version using localStorage as the source of truth.
 *
 * Flow:
 *  1. On first visit  → fetch server version → store in localStorage → no banner
 *  2. On each check   → fetch server version → compare with localStorage
 *  3. Different?      → show banner with the new version
 *  4. "Refresh Now"   → update localStorage with new version → reload page
 *  5. "Later"         → dismiss for this session (localStorage unchanged)
 *                       banner will reappear on next tab-focus / navigation
 *
 * Checks are event-driven (not a tight poll):
 *  • Tab becomes visible   (user switches back to this tab)
 *  • Window gains focus    (user returns to browser window)
 *  • Route change complete (user navigates to any page)
 *  • ChunkLoadError event  (stale chunk — fired by _app.tsx)
 *  • 30-min fallback       (safety net for completely idle sessions)
 */

const LS_KEY = 'app_build_version'
const FALLBACK_INTERVAL_MS = 10 * 60 * 1000 // 30 minutes
const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < responsiveBreakpointMap.md

/**
 * Formats technical build versions like "May_08_2026_1_00000" 
 * into human-friendly strings like "May 08, 2026 (v1.00000)"
 */
const formatVersion = (version: string | null) => {
    if (!version) return '';
    const parts = version.split('_');
    if (parts.length >= 3) {
        const month = parts[0];
        const day = parts[1];
        const year = parts[2];
        const build = parts.slice(3).join('.');

        let formatted = `${month} ${day}, ${year}`;
        if (build) {
            formatted += ` (v${build})`;
        }
        return formatted;
    }
    return version.replace(/_/g, ' ');
};

export const NewBuildBanner = () => {
    const [showBanner, setShowBanner] = useState(false)
    const [dismissed, setDismissed] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const dismissedRef = useRef(false)
    const checkingRef = useRef(false)
    const [newVersion, setNewVersion] = useState<string | null>(null)
    const [currentVersion, setCurrentVersion] = useState<string | null>(null)

    const language = useSelector((state: RootState) => state.language.language)
    const localeText = (locale[language as keyof typeof locale] || locale.en) as any
    const t = localeText.new_build_banner

    const checkVersion = async () => {
        if (isMobileViewport()) return
        if (checkingRef.current) return
        checkingRef.current = true
        try {
            const res = await fetch('/api/build-id', { cache: 'no-store' })
            if (!res.ok) return
            const data = await res.json()

            const serverVersion: string | undefined = data?.appVersion
            if (!serverVersion) return

            const clientVersion = process.env.NEXT_PUBLIC_BUILD_VERSION || pkgVersion

            // 1. If currently running code matches server → definitely up to date
            if (serverVersion === clientVersion) {
                localStorage.setItem(LS_KEY, serverVersion)
                setCurrentVersion(serverVersion)
                setShowBanner(false)
                dismissedRef.current = false
                setDismissed(false)
                return
            }

            const storedVersion = localStorage.getItem(LS_KEY)

            // 2. If no stored version → first visit, store client and exit silently
            if (!storedVersion) {
                localStorage.setItem(LS_KEY, clientVersion)
                setCurrentVersion(clientVersion)
                return
            }

            // 3. Check if server differs from our stored baseline → new build available
            if (serverVersion !== storedVersion) {
                setCurrentVersion(storedVersion)
                setNewVersion(serverVersion)
                if (!dismissedRef.current) {
                    setShowBanner(true)
                }
            } else {
                // Versions match stored baseline — hide banner
                setShowBanner(false)
            }
        } catch {
            // Network error — silently ignore
        } finally {
            checkingRef.current = false
        }
    }

    useEffect(() => {
        const handleResize = () => {
            const mobile = isMobileViewport()
            setIsMobile(mobile)
            if (mobile) setShowBanner(false)
        }

        handleResize()
        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (isMobile) return

        // Seed currentVersion from localStorage on mount
        const stored = localStorage.getItem(LS_KEY)
        if (stored) setCurrentVersion(stored)

        // ── Trigger 1: tab becomes visible ──
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') checkVersion()
        }

        // ── Trigger 2: window regains focus ──
        const handleFocus = () => checkVersion()

        // ── Trigger 3: Next.js route navigation completed ──
        const handleRouteChange = () => checkVersion()

        // ── Trigger 4: ChunkLoadError fired by _app.tsx ──
        const handleChunkError = () => {
            if (!dismissedRef.current) checkVersion()
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleFocus)
        window.addEventListener('new-build-available', handleChunkError)
        Router.events.on('routeChangeComplete', handleRouteChange)

        // ── Trigger 5: 30-min safety-net fallback ──
        const fallbackInterval = setInterval(checkVersion, FALLBACK_INTERVAL_MS)

        // Initial check (short delay so it doesn't block page load)
        const initialTimer = setTimeout(checkVersion, 8_000)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('new-build-available', handleChunkError)
            Router.events.off('routeChangeComplete', handleRouteChange)
            clearInterval(fallbackInterval)
            clearTimeout(initialTimer)
        }
    }, [isMobile])

    const handleRefresh = () => {
        setIsRefreshing(true)
        if (newVersion) {
            localStorage.setItem(LS_KEY, newVersion)
        }
        setTimeout(() => window.location.reload(), 400)
    }

    const handleDismiss = () => {
        dismissedRef.current = true
        setDismissed(true)
        setShowBanner(false)
    }

    if (isMobile || !showBanner || dismissed) return null

    return (
        <>
            <style>{`
                @keyframes nb-slide-down {
                    from { transform: translateY(-100%); opacity: 0; }
                    to   { transform: translateY(0);     opacity: 1; }
                }
                @keyframes nb-shimmer {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes nb-glow-pulse {
                    0%, 100% { box-shadow: 0 0 8px 2px rgba(255, 107, 0, 0.5); }
                    50%      { box-shadow: 0 0 16px 4px rgba(255, 107, 0, 0.85); }
                }
                @keyframes nb-border-glow {
                    0%, 100% { opacity: 0.4; }
                    50%      { opacity: 1; }
                }
                @keyframes nb-rocket-float {
                    0%, 100% { transform: translateY(0px) rotate(-5deg); }
                    50%      { transform: translateY(-3px) rotate(-5deg); }
                }
                @keyframes nb-progress {
                    from { width: 0%; }
                    to   { width: 100%; }
                }

                .nb-banner {
                    animation: nb-slide-down 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
                }
                .nb-accent-line {
                    animation: nb-border-glow 2.5s ease-in-out infinite;
                }
                .nb-rocket {
                    animation: nb-rocket-float 2s ease-in-out infinite;
                    display: inline-block;
                }
                .nb-refresh-btn {
                    position: relative;
                    overflow: hidden;
                    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease;
                    background-size: 200% auto;
                }
                .nb-refresh-btn::before {
                    content: '';
                    position: absolute;
                    top: 0; left: -100%;
                    width: 60%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
                    animation: nb-shimmer 2.2s linear infinite;
                }
                .nb-refresh-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(255, 107, 0, 0.55);
                }
                .nb-refresh-btn:active {
                    transform: translateY(0) scale(0.97);
                }
                .nb-refresh-btn.loading {
                    opacity: 0.75;
                    pointer-events: none;
                }
                .nb-dismiss-btn {
                    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
                }
                .nb-dismiss-btn:hover {
                    background: rgba(255,255,255,0.08) !important;
                    border-color: rgba(255,255,255,0.35) !important;
                    color: #fff !important;
                }
                .nb-dot {
                    animation: nb-glow-pulse 1.8s ease-in-out infinite;
                }
                .nb-progress-bar {
                    animation: nb-progress 0.38s ease forwards;
                }
            `}</style>

            <div
                className="nb-banner"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 9999,
                    width: '100%',
                    background: 'linear-gradient(100deg, #0d0d1a 0%, #111827 40%, #0f1f3d 100%)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Glowing top accent line */}
                <div
                    className="nb-accent-line"
                    style={{
                        height: 2,
                        background: 'linear-gradient(90deg, transparent 0%, #FF6B00 30%, #ff9a4d 60%, transparent 100%)',
                    }}
                />

                {/* Progress bar shown while refreshing */}
                {isRefreshing && (
                    <div
                        className="nb-progress-bar"
                        style={{
                            height: 2,
                            background: 'linear-gradient(90deg, #FF6B00, #ff9a4d)',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                        }}
                    />
                )}

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '9px 20px',
                    }}
                >
                    {/* Left: icon + text */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        {/* Glowing dot */}
                        <span
                            className="nb-dot"
                            style={{
                                flexShrink: 0,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: '#FF6B00',
                                display: 'inline-block',
                            }}
                        />

                        {/* Rocket */}
                        <span className="nb-rocket" style={{ fontSize: 16, flexShrink: 0 }}>🚀</span>

                        <div style={{ minWidth: 0 }}>
                            <p
                                style={{
                                    margin: 0,
                                    color: '#ffffff',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    lineHeight: 1.35,
                                    letterSpacing: '0.01em',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {newVersion
                                    ? <>
                                        <span style={{
                                            background: 'linear-gradient(90deg, #FF6B00, #ff9a4d)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                        }}>
                                            {t.title} {formatVersion(newVersion)}
                                        </span>
                                        {' '}
                                        <span style={{ color: '#fff' }}>{t.is_available}</span>
                                    </>
                                    : t.fallback_title}

                                {/* {currentVersion && (
                                    <span style={{
                                        fontWeight: 400,
                                        color: 'rgba(255,255,255,0.38)',
                                        marginLeft: 8,
                                        fontSize: 11,
                                        letterSpacing: '0.02em',
                                    }}>
                                        ({t.you_are_on} {formatVersion(currentVersion)})
                                    </span>
                                )} */}
                            </p>
                            <p
                                style={{
                                    margin: '2px 0 0',
                                    color: 'rgba(255,255,255,0.42)',
                                    fontSize: 11,
                                    lineHeight: 1.3,
                                }}
                            >
                                {t.desc}
                            </p>
                        </div>
                    </div>

                    {/* Right: buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <button
                            className={`nb-refresh-btn${isRefreshing ? ' loading' : ''}`}
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            style={{
                                border: 'none',
                                borderRadius: 7,
                                background: 'linear-gradient(135deg, #FF6B00 0%, #ff9a4d 100%)',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 12,
                                padding: '7px 16px',
                                cursor: 'pointer',
                                letterSpacing: '0.03em',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            <span style={{
                                display: 'inline-block',
                                animation: isRefreshing ? 'nb-shimmer 0.7s linear infinite' : 'none',
                                fontSize: 14,
                                lineHeight: 1,
                            }}>
                                {isRefreshing ? '⟳' : '↻'}
                            </span>
                            {isRefreshing ? t.refreshing : t.refresh_now}
                        </button>

                        {/* <button
                            className="nb-dismiss-btn"
                            onClick={handleDismiss}
                            title="Remind me later"
                            style={{
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 7,
                                background: 'transparent',
                                color: 'rgba(255,255,255,0.45)',
                                fontSize: 12,
                                fontWeight: 500,
                                padding: '6px 14px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                letterSpacing: '0.02em',
                            }}
                        >
                            {t.later}
                        </button> */}
                    </div>
                </div>
            </div>
        </>
    )
}

export default NewBuildBanner
